import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from '../services/axiosInstance';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { PrinterIcon, DownloadIcon, EyeIcon, FileTextIcon, ZoomInIcon, ZoomOutIcon, RotateCcwIcon, ArrowLeftIcon } from 'lucide-react';
import { getFreshPresignedUrl } from '../utils/s3';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ExpenseReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestId } = useParams();
  
  // State for data
  const [advanceRequest, setAdvanceRequest] = useState(location.state?.advanceRequest || null);
  const [expenseLines, setExpenseLines] = useState(location.state?.expenseLines || []);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]); // Cost center projects
  const [expenseTypes, setExpenseTypes] = useState([]); // Cost center expense types
  
  // Fetch fresh data from backend on mount
  useEffect(() => {
    const fetchFreshData = async () => {
      if (!requestId) return;
      
      try {
        setLoading(true);
        console.log('Fetching fresh expense data from backend for report...');
        
        // Fetch expenses, cost centers, and advance data in parallel
        const [expensesResponse, projectsResponse, expenseTypesResponse] = await Promise.all([
          axios.get(`${API}/advance-expenses/${requestId}`),
          axios.get(`${API}/cost-centers/projects`),
          axios.get(`${API}/cost-centers/expense-types`)
        ]);
        
        if (expensesResponse.data && expensesResponse.data.length > 0) {
          setExpenseLines(expensesResponse.data);
          console.log('Fresh expenses loaded for report:', expensesResponse.data);
        }
        
        setProjects(projectsResponse.data);
        setExpenseTypes(expenseTypesResponse.data);
        
        // If no advance request in state, fetch it too
        if (!advanceRequest) {
          const advanceResponse = await axios.get(`${API}/advance-requests/paid`);
          const request = advanceResponse.data.find(r => r.id === requestId);
          if (request) {
            setAdvanceRequest(request);
          }
        }
        
      } catch (error) {
        console.error('Error fetching fresh data for report:', error);
        toast.error('Veri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFreshData();
  }, [requestId]);
  
  // Console logs removed for cleaner output

  // File preview states
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  if (!advanceRequest || !expenseLines) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Harcama raporu verileri bulunamadı</p>
        <p className="text-sm text-gray-500 mt-2">Lütfen tarayıcının geri butonunu kullanın</p>
      </div>
    );
  }

  // Calculate totals
  const totalExpenses = expenseLines.reduce((sum, line) => {
    return sum + (parseFloat(line.amount) || 0);
  }, 0);

  // Format currency
  const formatCurrency = (amount, currency) => {
    // Handle null, undefined, or invalid amounts
    const numAmount = Number(amount) || 0;
    
    if (currency === 'USD') {
      return `$${numAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
    } else if (currency === 'EUR') {
      return `€${numAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
    }
    return `${numAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  // Get cost center name
  const getCostCenterName = (expense) => {
    if (!expense.cost_center_type || !expense.cost_center_id) {
      return '-';
    }
    
    if (expense.cost_center_type === 'project') {
      const project = projects.find(p => p.id === expense.cost_center_id);
      return project ? project.project_name : 'Proje bulunamadı';
    } else if (expense.cost_center_type === 'general_expense') {
      const expenseType = expenseTypes.find(t => t.id === expense.cost_center_id);
      return expenseType ? expenseType.expense_type_name : 'Gider türü bulunamadı';
    }
    
    return '-';
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Simple CSV export
    const headers = ['Tarih', 'İşletme', 'Kategori', 'Alt Kategori', 'Açıklama', 'Tutar', 'Birim', 'Belge Durumu'];
    const csvContent = [
      headers.join(','),
      ...expenseLines.map(line => [
        line.date,
        line.supplier || '-',
        line.category || '-',
        line.subcategory || '-',
        line.description || '-',
        line.amount || '0',
        line.currency,
        line.documentStatus
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `harcama_raporu_${advanceRequest.advance_number}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // File preview functions
  // File preview function - uses centralized S3 utility
  const previewFileAttachment = async (fileData) => {
    if (!fileData) {
      toast.error('Dosya verisi bulunamadı');
      return;
    }
    
    try {
      toast.loading('Belge yükleniyor...', { id: 'loading-file' });
      
      // Get fresh presigned URL using centralized utility
      const presignedUrl = await getFreshPresignedUrl(fileData, 3600);
      
      toast.dismiss('loading-file');
      
      if (!presignedUrl) {
        throw new Error('Presigned URL oluşturulamadı');
      }
      
      // Set preview file with fresh presigned URL
      // IMPORTANT: spread fileData FIRST so the freshly-generated `url`
      // is not overwritten by an expired URL stored on fileData.
      const safeFileData = {
        ...fileData,
        name: fileData.name || fileData.original_filename || 'Bilinmeyen dosya',
        type: fileData.type || fileData.content_type || 'application/octet-stream',
        url: presignedUrl,
        preview: presignedUrl,
      };
      
      setPreviewFile(safeFileData);
      setShowFilePreview(true);
      setImageScale(1);
      setImagePosition({ x: 0, y: 0 });
    } catch (error) {
      toast.dismiss('loading-file');
      console.error('Belge yükleme hatası:', error);
      toast.error('Dosya URL\'si oluşturulamadı: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleImageWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.max(0.5, Math.min(3, imageScale + delta));
    setImageScale(newScale);
  };

  const handleImageMouseDown = (e) => {
    if (imageScale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y
      });
    }
  };

  const handleImageMouseMove = (e) => {
    if (isDragging && imageScale > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  const resetImageView = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const zoomIn = () => {
    const newScale = Math.min(3, imageScale + 0.2);
    setImageScale(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(0.5, imageScale - 0.2);
    setImageScale(newScale);
    if (newScale === 1) {
      setImagePosition({ x: 0, y: 0 });
    }
  };

  return (
    <div className="space-y-6 print:space-y-4" id="expense-report">
      {/* Back Button - Top Left */}
      <div className="print:hidden">
        <Button 
          onClick={() => {
            console.log('Geri dön clicked - checking navigation source');
            console.log('Location state:', location.state);
            
            // Check if we came from ClosedAdvances page
            if (location.state?.fromClosedAdvances) {
              console.log('Navigating back to Closed Advances');
              navigate('/dashboard/closed');
            } else {
              // Default behavior - go to advance closing detail
              console.log('Geri dön clicked - navigating to advance closing detail');
              console.log('Carrying advance data:', advanceRequest);
              navigate(`/dashboard/advance-closing/${requestId}`, {
                state: {
                  forceReload: true,
                  message: 'Harcama raporundan geri döndünüz',
                  advanceData: advanceRequest, // Carry advance data for fallback
                  expenseData: expenseLines    // Also carry expense data for context
                }
              });
            }
          }}
          variant="outline"
          className="mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          GERİ DÖN
        </Button>
      </div>

      {/* Header - No Print */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 print:hidden">
        <div className="flex items-center space-x-4 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center text-xs sm:text-sm text-gray-500 mb-2 flex-wrap">
              <span>Avans Kapama</span>
              <span className="mx-2">›</span>
              <span>Harcama Detayı</span>
              <span className="mx-2">›</span>
              <span className="text-gray-900 font-medium">Harcama Raporu</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Harcama Raporu</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
              {advanceRequest.advance_number} - {advanceRequest.requester_name}
            </p>
          </div>
        </div>
        <div className="flex space-x-2 flex-shrink-0">
          <Button onClick={handlePrint} variant="outline" size="sm">
            <PrinterIcon className="w-4 h-4 mr-2" />
            Yazdır
          </Button>
          <Button onClick={handleDownload} variant="outline" size="sm">
            <DownloadIcon className="w-4 h-4 mr-2" />
            CSV İndir
          </Button>
        </div>
      </div>

      {/* Report Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 print:border-0 print:shadow-none">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">ADVANCECASH.IO</h2>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-700">HARCAMA RAPORU</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Avans Bilgileri</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Avans Numarası:</span>
                <span className="font-medium">{advanceRequest.advance_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Talep Eden:</span>
                <span className="font-medium">{advanceRequest.requester_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Proje:</span>
                <span className="font-medium">{advanceRequest.project || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Kategori:</span>
                <span className="font-medium">{advanceRequest.category || '-'}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Finansal Özet</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Talep Edilen Tutar:</span>
                <span className="font-medium">{formatCurrency(advanceRequest.amount, advanceRequest.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Ödenen Avans:</span>
                <span className="font-medium">{formatCurrency(advanceRequest.approved_amount, advanceRequest.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Toplam Harcama:</span>
                <span className="font-medium text-red-600">{formatCurrency(totalExpenses, advanceRequest.currency)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-900 font-semibold">Kalan Bakiye:</span>
                <span className={`font-bold ${advanceRequest.approved_amount - totalExpenses >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(advanceRequest.approved_amount - totalExpenses, advanceRequest.currency)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500 mb-6">
          Rapor Tarihi: {new Date().toLocaleDateString('tr-TR')}
        </div>
      </div>

      {/* Expense Table */}
      <div className="bg-white rounded-lg border border-gray-200 print:border-0 print:shadow-none">
        <div className="px-6 py-4 border-b border-gray-200 print:border-b-2 print:border-black">
          <h3 className="text-lg font-semibold text-gray-900">Harcama Kalemleri</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 print:bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Tarih
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  İşletme
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Kategori
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Alt Kategori
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Açıklama
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Tutar
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Masraf Merkezi
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Ödeme Yöntemi
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b print:border-b-2 print:border-black">
                  Belge
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 print:divide-y-2 print:divide-black">
              {expenseLines.map((line, index) => (
                <tr key={line.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 print:bg-gray-100'}>
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {index + 1}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {formatDate(line.date)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {line.supplier || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {line.category || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {line.subcategory || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {line.description || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right print:py-2">
                    {formatCurrency(parseFloat(line.amount) || 0, line.currency)}
                  </td>
                  
                  {/* Cost Center Column */}
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {getCostCenterName(line)}
                  </td>
                  
                  {/* Payment Method Column */}
                  <td className="px-4 py-3 text-sm text-gray-900 print:py-2">
                    {(() => {
                      const paymentMethod = line.payment_method || line.paymentMethod || 'cash';
                      
                      if (paymentMethod === 'cash') {
                        return <span className="text-gray-600">Nakit</span>;
                      } else if (paymentMethod === 'credit_card') {
                        const cardDetails = line.credit_card_details || line.creditCardDetails;
                        if (cardDetails) {
                          const bankName = cardDetails?.bank_name || cardDetails?.bankName;
                          const last4 = cardDetails?.card_last_4_digits || cardDetails?.cardLast4Digits;
                          return (
                            <div className="text-xs">
                              <div className="font-medium text-gray-900">Kredi Kartı</div>
                              <div className="text-gray-600">{bankName} •••• {last4}</div>
                            </div>
                          );
                        }
                        return <span className="text-gray-600">Kredi Kartı</span>;
                      } else if (paymentMethod === 'bank_transfer') {
                        const transferDetails = line.bank_transfer_details || line.bankTransfer;
                        const bankName = transferDetails?.bank_name || transferDetails?.bankName;
                        if (bankName) {
                          return (
                            <div className="text-xs">
                              <div className="font-medium text-gray-900">Banka Transferi</div>
                              <div className="text-gray-600">{bankName}</div>
                            </div>
                          );
                        }
                        return <span className="text-gray-600">Banka Transferi</span>;
                      }
                      return <span className="text-gray-400">-</span>;
                    })()}
                  </td>
                  
                  <td className="px-4 py-3 text-sm text-center print:py-2">
                    <div className="flex items-center justify-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        line.documentStatus === 'Belgeli' 
                          ? 'bg-green-100 text-green-800 print:bg-white print:border print:border-green-600' 
                          : 'bg-red-100 text-red-800 print:bg-white print:border print:border-red-600'
                      }`}>
                        {line.documentStatus}
                      </span>
                      {line.documentStatus === 'Belgeli' && (() => {
                        const fileData = line.attachedFile || line.attached_file;
                        
                        // DEBUG: Log file data to see what we have
                        if (line.documentStatus === 'Belgeli') {
                          console.log('🔍 Belgeli expense:', {
                            id: line.id,
                            documentStatus: line.documentStatus,
                            attachedFile: line.attachedFile,
                            attached_file: line.attached_file,
                            fileData: fileData,
                            hasS3Key: fileData?.s3_key,
                            hasPath: fileData?.path,
                            hasUrl: fileData?.url,
                            allLineData: line
                          });
                        }
                        
                        // Check for any file identifier (S3 key, path, or URL)
                        const hasFile = fileData && (
                          fileData.s3_key || 
                          fileData.path || 
                          fileData.s3_path || 
                          fileData.key || 
                          fileData.url ||
                          fileData.name // Even just a name means file was attached
                        );
                        
                        if (hasFile) {
                          return (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 print:hidden cursor-pointer"
                              data-preview-eye="true"
                              aria-label="Belgeyi önizle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                previewFileAttachment(fileData);
                              }}
                            >
                              <EyeIcon className="w-4 h-4 text-blue-600 hover:text-blue-800" />
                            </Button>
                          );
                        }
                        
                        return null;
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 print:bg-gray-100">
              <tr className="border-t-2 border-gray-300 print:border-t-4 print:border-black">
                <td colSpan="6" className="px-4 py-3 text-sm font-bold text-gray-900 text-right print:py-2">
                  TOPLAM:
                </td>
                <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right print:py-2">
                  {formatCurrency(totalExpenses, advanceRequest.currency)}
                </td>
                <td className="px-4 py-3 print:py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Footer - Print Only */}
      <div className="hidden print:block text-center text-xs text-gray-500 mt-8">
        <p>Bu rapor Advancecash.io sistem tarafından otomatik olarak oluşturulmuştur.</p>
        <p>Yazdırma Tarihi: {new Date().toLocaleString('tr-TR')}</p>
      </div>

      {/* File Preview Modal */}
      <Dialog open={showFilePreview} onOpenChange={(open) => {
        setShowFilePreview(open);
        if (!open) {
          setPreviewFile(null);
          resetImageView();
        }
      }}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Belge Önizleme</span>
              {previewFile && previewFile.type && previewFile.type.startsWith('image/') && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={zoomOut}
                    disabled={imageScale <= 0.5}
                  >
                    <ZoomOutIcon className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-500 min-w-[60px] text-center">
                    {Math.round(imageScale * 100)}%
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={zoomIn}
                    disabled={imageScale >= 3}
                  >
                    <ZoomInIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetImageView}
                  >
                    <RotateCcwIcon className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {previewFile && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Dosya: {previewFile.name}
                </p>
                {previewFile.type && previewFile.type.startsWith('image/') ? (
                  previewFile.url ? (
                  <div 
                    className="relative border rounded-lg overflow-hidden bg-gray-50"
                    style={{ height: '500px' }}
                  >
                    <div
                      className="w-full h-full overflow-hidden cursor-move"
                      onWheel={handleImageWheel}
                      onMouseDown={handleImageMouseDown}
                      onMouseMove={handleImageMouseMove}
                      onMouseUp={handleImageMouseUp}
                      onMouseLeave={handleImageMouseUp}
                      style={{
                        cursor: imageScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                      }}
                    >
                      <img
                        src={previewFile?.url || previewFile?.path || previewFile?.s3_path || previewFile?.name}
                        alt={previewFile?.name || 'Dosya'}
                        onError={(e) => {
                          console.error('Image load error:', e);
                          console.log('Image URL:', previewFile.url);
                        }}
                        onLoad={() => console.log('Image loaded successfully:', previewFile.url)}
                        className="transition-transform duration-100 select-none"
                        style={{
                          transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                          transformOrigin: 'center center',
                          maxWidth: 'none',
                          maxHeight: 'none',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                        draggable={false}
                      />
                    </div>
                    {imageScale > 1 && (
                      <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                        Sürükleyerek hareket ettirin
                      </div>
                    )}
                    <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                      Mouse wheel ile zoom yapın
                    </div>
                  </div>
                  ) : (
                    <div className="bg-gray-100 p-8 rounded-lg">
                      <FileTextIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">Dosya URL'si bulunamadı</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Dosya yüklenirken bir hata oluşmuş olabilir.
                      </p>
                    </div>
                  )
                ) : previewFile.type === 'application/pdf' ? (
                  <div className="bg-gray-100 p-8 rounded-lg">
                    <FileTextIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">PDF Dosyası</p>
                    <p className="text-sm text-gray-500 mt-2">
                      PDF önizlemesi tarayıcınızda desteklenmiyor.
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-100 p-8 rounded-lg">
                    <FileTextIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">Belge Önizlemesi</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Bu dosya türü için önizleme desteklenmiyor.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowFilePreview(false);
                setPreviewFile(null);
                resetImageView();
              }}
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #expense-report, #expense-report * {
            visibility: visible;
          }
          #expense-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:border-0 {
            border: 0 !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:py-2 {
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
          }
          .print\\:border-b-2 {
            border-bottom-width: 2px !important;
          }
          .print\\:border-black {
            border-color: black !important;
          }
          .print\\:divide-y-2 > * + * {
            border-top-width: 2px !important;
          }
          .print\\:divide-black > * + * {
            border-top-color: black !important;
          }
          .print\\:border-t-4 {
            border-top-width: 4px !important;
          }
          .print\\:bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
          .print\\:border {
            border-width: 1px !important;
          }
          .print\\:border-green-600 {
            border-color: #059669 !important;
          }
          .print\\:border-red-600 {
            border-color: #dc2626 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ExpenseReport;